/**
 * Step 3A: 画像アップロードステップ
 *
 * 画像ファイルを選択してアイコンとしてアップロード
 */
import { useRef, useState, ChangeEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { uploadImageFile } from "app/api";
import I18n from "common/i18n";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { UserAvatar } from "common/elements";
import { CreateUserState } from "../types";

interface UploadIconStepProps {
  state: CreateUserState;
  updateState: (updates: Partial<CreateUserState>) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

export const UploadIconStep = ({ state, updateState }: UploadIconStepProps) => {
  const play = usePlaySound();
  const handleDialog = useDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");

  const handleFileSelect = (file: File) => {
    setError("");

    // 画像ファイルのみ許可
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(I18n.t("MSG_UPLOAD_ICON_TYPE_ERROR"));
      return;
    }

    // ファイルサイズ制限（5MB）
    if (file.size > MAX_FILE_SIZE) {
      setError(I18n.t("MSG_UPLOAD_ICON_SIZE_ERROR"));
      return;
    }

    // S3にアップロード
    const uuid = uuidv4();
    const filename = `users/icons/${uuid}.png`;
    const url = `${Constants.assetHost}/${filename}`;

    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      handleDialog({ type: DialogType.LOADING, value: true });
      uploadImageFile({
        filename,
        fileBlob: file,
        metadata: {
          filename: encodeURIComponent(file.name),
          size: String(file.size),
          width: String(image.width),
          height: String(image.height),
        },
        contentType: file.type,
      })
        .then(() => {
          updateState({
            icon: url,
            iconMetadata: {
              source: "uploaded" as const,
              filename: encodeURIComponent(file.name),
              content_type: file.type,
              file_size: file.size,
              width: String(image.width),
              height: String(image.height),
            },
          });
        })
        .catch(() => {
          setError(I18n.t("MSG_TOAST_ERROR"));
        })
        .finally(() => {
          handleDialog({ type: DialogType.LOADING, value: false });
          URL.revokeObjectURL(objectUrl);
        });
    };
    image.onerror = () => {
      setError(I18n.t("MSG_UPLOAD_ICON_TYPE_ERROR"));
      URL.revokeObjectURL(objectUrl);
    };
    image.src = objectUrl;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    play();
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleSelectFile = () => {
    play();
    fileInputRef.current?.click();
  };

  return (
    <div className="flex-col-el flex-center w-full h-full">
      {/* タイトル */}
      <div className="flex-col-el flex-center mb-8">
        <p className="text-body text-textcolor-black text-[32px]">
          {I18n.t("MSG_UPLOAD_ICON_TITLE")}
        </p>
      </div>

      {/* 隠しファイル入力 */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        onChange={handleInputChange}
        className="hidden"
      />

      {/* アップロードエリア */}
      <div className="flex-col-el flex-center">
        {state.icon ? (
          // プレビュー表示（アイコンクリックで再アップロード）
          <div className="relative cursor-pointer group" onClick={handleSelectFile}>
            <UserAvatar size={200} icon={state.icon} />
            <div className="absolute bottom-0 right-0 w-16 h-16 rounded-full bg-gray2-100 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-[54px] text-white">photo_camera</span>
            </div>
          </div>
        ) : (
          // アップロードボタン
          <div
            onClick={handleSelectFile}
            className="flex-col-el flex-center w-[280px] h-[280px] rounded-[8px] border-2 border-dashed border-green-60 bg-surface cursor-pointer select-none active:scale-95"
          >
            <span className="material-symbols-outlined !text-6xl text-green-60 mb-4">
              cloud_upload
            </span>
            <span className="text-[20px] text-green-60 text-center px-4">
              {I18n.t("MSG_UPLOAD_ICON_SELECT")}
            </span>
            <span className="text-[14px] text-textcolor-black/50 text-center mt-2 px-4">
              {I18n.t("MSG_UPLOAD_ICON_HINT")}
            </span>
          </div>
        )}
      </div>

      {/* エラーメッセージ */}
      {error && <p className="text-[14px] text-danger mt-4 text-center">{error}</p>}
    </div>
  );
};
